import { useEffect, useState } from "react";
import { FileText, Receipt, Loader2, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { formatMoney } from "../../lib/formatters";

type Document = {
  id: string;
  documentNumber: string | null;
  documentType: string;
  status: string;
  totalAmount: number;
  currency: string;
  issueDate: string | null;
  dueDate: string | null;
  customerName: string | null;
  organizationName: string | null;
  auctionReference: string | null;
  vehicleRegistrationNumber: string | null;
};

function getDocumentTypeLabel(documentType: string) {
  if (documentType === "credit_note") return "Kreditnota";
  if (documentType === "purchase_settlement") return "Kjøpsoppgjør";
  return "Faktura";
}

type OrderLine = {
  id: string;
  description: string;
  status: string;
  lineType: string;
  totalAmount: number;
  currency: string;
  createdAt: string;
  accountingDocumentId: string | null;
  accountingDocumentStatus: string | null;
  accountingDocumentPaidAt: string | null;
  organizationName: string | null;
  auctionReference: string | null;
  vehicleRegistrationNumber: string | null;
};

export function RegnskapPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<"documents" | "lines">("documents");
  const [documents, setDocuments] = useState<Document[]>([]);
  const [lines, setLines] = useState<OrderLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const api = import.meta.env.VITE_API_URL || "http://localhost:9000";
        const endpoint = tab === "documents" ? "documents" : "order-lines";
        const res = await fetch(`${api}/api/admin/accounting/${endpoint}`, {
          credentials: "include",
        });
        if (!res.ok) throw new Error("Kunne ikke hente data");
        const data = await res.json();
        if (tab === "documents") setDocuments(data);
        else setLines(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Noe gikk galt");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [tab]);

  const getOrderLinePaymentStatus = (line: OrderLine) => {
    if (!line.accountingDocumentId) {
      return {
        label: "Ikke fakturert",
        className: "bg-gray-100 text-gray-700",
      };
    }

    if (line.accountingDocumentStatus === "paid") {
      return {
        label: "Betalt",
        className: "bg-green-100 text-green-700",
      };
    }

    return {
      label: "Fakturert",
      className: "bg-blue-100 text-blue-700",
    };
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <div className="flex flex-col gap-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-black flex items-center gap-3">
              <Receipt className="h-8 w-8 text-accent" />
              Regnskap
            </h1>
            <p className="text-muted-foreground mt-1 text-sm">
              Oversikt over alle fakturaer, kreditnotaer og ordrelinjer i systemet.
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-1 bg-muted rounded-xl w-fit">
          <button
            onClick={() => setTab("documents")}
            className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${tab === "documents" ? "bg-white text-primary shadow-sm" : "hover:text-primary/70"
              }`}
          >
            Dokumenter
          </button>
          <button
            onClick={() => setTab("lines")}
            className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${tab === "lines" ? "bg-white text-primary shadow-sm" : "hover:text-primary/70"
              }`}
          >
            Ordrelinjer
          </button>
        </div>

        {error && (
          <div className="p-4 bg-red-50 text-red-700 rounded-xl border border-red-100 flex items-center gap-3">
            <span className="font-semibold">{error}</span>
          </div>
        )}

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3 text-muted-foreground">
            <Loader2 className="h-8 w-8 animate-spin" />
            <p className="font-medium">Laster inn regnskapsdata...</p>
          </div>
        ) : tab === "documents" ? (
          <div className="bg-white rounded-2xl border border-border overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="px-5 py-4 text-xs font-bold uppercase tracking-wider">Nr / Type</th>
                    <th className="px-5 py-4 text-xs font-bold uppercase tracking-wider">Motpart (Kunde)</th>
                    <th className="px-5 py-4 text-xs font-bold uppercase tracking-wider">Auksjon</th>
                    <th className="px-5 py-4 text-xs font-bold uppercase tracking-wider">Dato / Forfall</th>
                    <th className="px-5 py-4 text-xs font-bold uppercase tracking-wider">Status</th>
                    <th className="px-5 py-4 text-xs font-bold uppercase tracking-wider text-right">Beløp</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {documents.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-5 py-10 text-center text-muted-foreground">
                        Ingen dokumenter funnet
                      </td>
                    </tr>
                  ) : (
                    documents.map((doc) => (
                      <tr
                        key={doc.id}
                        className="hover:bg-muted/30 transition-colors group cursor-pointer"
                        onClick={() => navigate(`/regnskap/${doc.id}`)}
                      >
                        <td className="px-5 py-4">
                          <div className="flex flex-col">
                            <span className="font-bold text-sm">{doc.documentNumber || "Utkast"}</span>
                            <span className="text-[10px] uppercase font-black text-muted-foreground mt-0.5">
                              {getDocumentTypeLabel(doc.documentType)}
                            </span>
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex flex-col max-w-[200px]">
                            <span className="font-semibold text-sm truncate">{doc.customerName || "Ukjent"}</span>
                            <span className="text-xs text-muted-foreground truncate">{doc.organizationName}</span>
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex flex-col gap-1">
                            <span className="text-xs font-mono font-bold bg-muted px-2 py-1 rounded w-fit">
                              {doc.auctionReference || "—"}
                            </span>
                            <span className="text-[11px] text-muted-foreground font-semibold">
                              {doc.vehicleRegistrationNumber ? `Reg.nr. ${doc.vehicleRegistrationNumber}` : "Ingen reg.nr."}
                            </span>
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex flex-col text-xs">
                            <span>{doc.issueDate ? new Date(doc.issueDate).toLocaleDateString("nb-NO", { day: "2-digit", month: "short", year: "numeric" }) : "—"}</span>
                            <span className="text-muted-foreground">
                              Forfall: {doc.dueDate ? new Date(doc.dueDate).toLocaleDateString("nb-NO", { day: "2-digit", month: "2-digit", year: "2-digit" }) : "—"}
                            </span>
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <span className={`text-[10px] font-black uppercase px-2 py-1 rounded-full ${doc.status === "paid" ? "bg-green-100 text-green-700" :
                              doc.status === "sent" ? "bg-blue-100 text-blue-700" :
                                "bg-gray-100 text-gray-700"
                            }`}>
                            {doc.status}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-right">
                          <div className="flex items-center justify-end gap-3">
                            <span className="font-bold text-sm">
                              {formatMoney(doc.totalAmount, doc.currency)}
                            </span>
                            <ArrowRight className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity text-accent" />
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-border overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="px-5 py-4 text-xs font-bold uppercase tracking-wider">Beskrivelse</th>
                    <th className="px-5 py-4 text-xs font-bold uppercase tracking-wider">Type</th>
                    <th className="px-5 py-4 text-xs font-bold uppercase tracking-wider">Motpart / Auksjon</th>
                    <th className="px-5 py-4 text-xs font-bold uppercase tracking-wider">Opprettet</th>
                    <th className="px-5 py-4 text-xs font-bold uppercase tracking-wider">Status</th>
                    <th className="px-5 py-4 text-xs font-bold uppercase tracking-wider text-right">Beløp</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {lines.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-5 py-10 text-center text-muted-foreground">
                        Ingen ordrelinjer funnet
                      </td>
                    </tr>
                  ) : (
                    lines.map((line) => {
                      const paymentStatus = getOrderLinePaymentStatus(line);

                      return (
                        <tr key={line.id} className="hover:bg-muted/30 transition-colors">
                          <td className="px-5 py-4">
                            <span className="font-semibold text-sm">{line.description}</span>
                          </td>
                          <td className="px-5 py-4">
                            <span className="text-[10px] uppercase font-black text-muted-foreground">
                              {line.lineType}
                            </span>
                          </td>
                          <td className="px-5 py-4">
                            <div className="flex flex-col">
                              <span className="text-xs font-bold truncate">{line.organizationName}</span>
                              <span className="text-[10px] text-muted-foreground font-mono mt-0.5">{line.auctionReference || "—"}</span>
                              <span className="text-[10px] text-muted-foreground font-semibold mt-0.5">
                                {line.vehicleRegistrationNumber ? `Reg.nr. ${line.vehicleRegistrationNumber}` : "Ingen reg.nr."}
                              </span>
                            </div>
                          </td>
                          <td className="px-5 py-4">
                            <span className="text-xs">{new Date(line.createdAt).toLocaleString("nb-NO", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                          </td>
                          <td className="px-5 py-4">
                            <span className={`text-[10px] font-black uppercase px-2 py-1 rounded-full ${paymentStatus.className}`}>
                              {paymentStatus.label}
                            </span>
                          </td>
                          <td className="px-5 py-4 text-right font-bold text-sm">
                            {formatMoney(line.totalAmount, line.currency)}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
